<template>
  <div>
    <v-form ref="form">
      <v-card class="pa-5 pa-sm-10" elevation="0">
        <v-row>
          <v-col>
            <v-row no-gutters align="center">
              <h4 class="greyDark--text font-weight-bold mb-0">
                {{ $t('common:contract_plural') }}
              </h4>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs}">
                  <v-btn class="ml-2" icon @click="cardView = !cardView" v-bind="attrs" v-on="on">
                    <v-icon>list_alt</v-icon>
                  </v-btn>
                </template>
                <span v-if="cardView">{{ $t('buttons:table-view') }}</span>
                <span v-else>{{ $t('buttons:card-view') }}</span>
              </v-tooltip>
            </v-row>
          </v-col>
          <v-spacer></v-spacer>
          <v-col v-if="scoreBreak">
            <RadialScore ref="contractScore" :score="score" score-icon="contract"/>
          </v-col>
          <v-spacer></v-spacer>
          <v-col class="text-right">
            <BaseButton
                color="primary"
                :btnText="$t('buttons:add-specific', { specific: $t('common:contract')})"
                @clickedButton="openFormDialog()"
            />
          </v-col>
          <v-col cols="12" v-if="!scoreBreak">
            <RadialScore ref="contractScore" :score="score" score-icon="contract"/>
          </v-col>
          <v-col cols="12" class="pt-0" v-show="!cardView">            
            <DataTablePaginated
                ref="contractTable"
                class="ma-1"
                :headers="contractHeader"
                icon="create"
                icon2="delete"
                iconColor2="error"
                :iconTooltip="$t('buttons:edit')"
                :iconTooltip2="$t('buttons:delete')"
                @clickedButton1="editContract"
                @clickedButton2="deleteContract"
                @updateTableData="updateContractTable"
                sortBy="name"
                checkbox-on-property="contractValid"
                @cacheCommit="$store.commit('book/facility/storeFacilityContractSearch', $event)"
                :cachedSearch="$store.state.book.facility.contractSearch"
            />
          </v-col>
        </v-row>

        <div v-show="cardView">
          <SearchAndSortBar
            @updateData="updateContractTable"
            @cacheCommit="$store.commit('book/facility/storeFacilityContractSearch', $event)"
            :cachedSearch="$store.state.book.facility.contractSearch"
            :sorts="contractHeader"
            disable-filter
            default-sort-by="name"
          />
          <v-row>
            <v-col cols="12" md="6" :lg="cardSize" xl="4" v-for="item in requiredContracts" :key="item.name">
              <ItemCard
                  :item="item"
                  :title="item.translatedName"
                  hide-action-btn1
                  hide-action-btn2
                  hide-action-btn3
                  hide-chart
                  hide-view-btn
                  card-elevation
                  required
                  height="200"
                  :required-btn-text="$t('buttons:add-specific', {specific: $t('common:contract')})"
                  @viewItem="setRequiredContract(item)"
              />
            </v-col>
            <v-col cols="12" md="6" :lg="cardSize" xl="4" v-for="(item, i) in restContracts.content" :key="i" >
              <ItemCard
                  :item="item"
                  :title="item.name"
                  :subtitle="getAllContractTypes(item)"
                  :subtitle2="(item.contractEnd && item.contractStart) ?
                    $t('book/facility:facility.dashboard.information.contracts.contract-period') + ': ' + item.contractStart + ' - ' + item.contractEnd : null"
                  hideActionBtn1
                  hideActionBtn2
                  hideActionBtn3
                  hideChart
                  hideViewBtn
                  cardElevation
                  showDelete
                  @viewItem="editContract(item)"
                  @deleteItem="deleteContract(item)"
                  :errorBadgeText="!item.contractValid ?$t('book/facility:facility.dashboard.information.contracts.contract-datatable.invalid-badge') : null"
                  :warningTitle="checkValidType(item)"
                  height="200"
                  :was-required="item.wasRequired"
              />
            </v-col>
          </v-row>
          <v-pagination v-show="this.restContracts.content.length != 0"
            v-model="restContracts.options.page"
            :length="totalPages"
            :total-visible="7">
          </v-pagination>
        </div>
        <v-col v-if="restContracts.content.length == 0 && restRequiredContracts.length == 0" class="text-center">
            <h6 class="greyTint--text mb-0">
              {{ $t('common:no-data-found') }}
            </h6>
        </v-col>          
      </v-card>
    </v-form>

    <FacilityContractDialogForm
        ref="contractForm"
        @updateContractData="updateContractTable"
        @changedItem="() => $emit('changedItem')"
    />

    <TwoButtonDialog
        ref="deleteContract"
        :btn-text="$t('buttons:delete')"
        color="grey"
        :dialog-title="$t('dialog:delete-btn-title',{name : $t('book/facility:facility.dashboard.information.contracts.dialog-part-1')})"
        :dialog-text="$t('dialog:delete-btn-dialog',{name : $t('book/facility:facility.dashboard.information.contracts.dialog-part-2')})"
        :button1-text="$t('buttons:cancel')"
        :button2-text="$t('buttons:delete')"
        @clickedButton1="$refs.deleteContract.showDialog = false"
        @clickedButton2="deleteContractConfirmed()"
        hide-button
    />
  </div>
</template>
<script>

import ContractInfoService from "@/modules/book/services/contract-info-service";

import Utils from "@/modules/core/services/utils";

import ItemCard from '@/modules/core/components/ItemCard'
import DataTablePaginated from "@/modules/core/components/DataTable/DataTablePaginated"
import FacilityContractDialogForm from "../formDialogs/FacilityContractDialogForm"
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import SearchAndSortBar from "@/modules/core/components/SearchAndSortBar";

import {mapState} from 'vuex'
import RadialScore from "@/modules/core/components/RadialScore";
import Score from "@/modules/book/data/score";
import FacilityTypeService from "@/modules/book/services/facility-type-service";
import EnumService from "@/modules/core/services/enum-service";
import DataUtils from "@/modules/core/services/data-utils";

export default {
  name: 'FacilityContractInfoTab',
  components: {
    RadialScore,
    ItemCard,
    DataTablePaginated,
    FacilityContractDialogForm,
    TwoButtonDialog,
    BaseButton,
    SearchAndSortBar
  },
  data() {
    return {
      cardView: true,
      restContracts: {
        options: [],
        search: [],
        content: []
      },
      totalPages: null,
      restRequiredContracts: [],
      score: new Score()
    }
  },
  computed: {
    contractHeader() {
      return [
        {text: this.$t('book/facility:facility.dashboard.information.contracts.labels.contract-name'), value: "name"},
        {text: this.$t('book/facility:facility.dashboard.information.contracts.contract-datatable.valid'), value: "checkbox"},
        {text: "", value: 'controls', sortable: false}
      ]
    },
    cardSize() {
      // if both sidebars are fully opened, make card size bigger
      if(this.leftFacilityDrawer && this.rightFacilityDrawer) {
        return '6'
        // if both side bars are closed, make card size smaller
      } else if(!this.leftFacilityDrawer && !this.rightFacilityDrawer) {
        return '3'
      } else {
        return '4'
      }
    },

    scoreBreak() {
      // if both sidebars are fully opened and the display size is medium
      if (this.leftFacilityDrawer && this.rightFacilityDrawer && this.$vuetify.breakpoint.mdAndDown) {
        return true;
      } else if (!this.leftFacilityDrawer && !this.rightFacilityDrawer) {
        return false;
      } else {
        return false;
      }
    },

    ...mapState('book/layout', {
      leftFacilityDrawer: state => state.leftFacilityDrawer,
      rightFacilityDrawer: state => state.rightFacilityDrawer
    }),

    requiredContracts() {
      if (this.restContracts.options.page === 1) {
        return this.restRequiredContracts;
      } else {
        return []
      }
    }
  },
  methods: {
    getAllContractTypes(item) {
      let typeStrings;
      if (item.translatedTypes.length > 1) {
        typeStrings = this.$t('book/facility:facility.dashboard.information.contracts.common.contract-type') +
            ": " + item.translatedTypes[0] + ", ";

        for (let i = 1; i < item.translatedTypes.length - 1; i++) {
          typeStrings += item.translatedTypes[i] + ", ";
        }

        typeStrings += item.translatedTypes[item.translatedTypes.length - 1];
      } else {
        typeStrings = this.$t('book/facility:facility.dashboard.information.contracts.common.contract-type') +
            ": " + item.translatedTypes[0];
      }

      return typeStrings;
    },

    checkValidType(item) {
      return !item.types || item.types.length === 0;
    },

    updateContractTable(options, search) {      

      this.restContracts.options = options;
      this.restContracts.search = search;
      if(!search) search = '';

      if (!this.cardView) {
        this.$refs.contractTable.loading = true;
      }

      ContractInfoService.readAllPaginatedFromAPI(
        this.$route.params.id,
        options.page - 1,
        options.itemsPerPage,
        search,
        options.sortBy,
        options.sortDesc,
        (res) => {
          res.data.content.forEach((contract) => {
            contract.translatedTypes = [];
            contract.types.forEach((type) => {
              contract.translatedTypes.push(this.$t('enums:contract-types.' + type))
            })
            contract.warning = !contract.types || contract.types.length === 0;
          })

          // we have to manually sort here, because we translate the enums in the frontend
          if (options.sortBy[0] === 'checkbox') {
            res.data.content = Utils.sort(res.data.content, options.sortDesc[0], "contractValid");
          }

          if (!this.cardView) {
            this.$refs.contractTable.fillTableData(res.data);
            this.$refs.contractTable.loading = false;
          } else {
            this.restContracts.content = res.data.content;
            this.getRequiredContracts()
            this.totalPages = res.data.totalPages;
          }

          this.getScore();
          this.$emit('updateQuicklinkContracts')
        },
        (error) => {
          if (!this.cardView) {
            this.$refs.contractTable.loading = false;
          }
          this.$logger.error("Error at fetching facility contracts: " + error);
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/facility:contract.snackbar.contract')}))
        }
      )
    },
    editContract(item) {
      this.$refs.contractForm.editContract(item, this.restContracts.options, this.restContracts.search)
    },

    deleteContract(item) {
      this.deleteContractId = item.id;
      this.$refs.deleteContract.showDialog = true;
    },

    deleteContractConfirmed() {
      ContractInfoService.deleteFromAPI(this.deleteContractId,
          () => {
        this.updateContractTable(this.restContracts.options, this.restContracts.search);
        Utils.showSuccessSnackbar(this.$t('snackbar:delete-success', {name: this.$t('common:documentation')}))
      },
      (error) => {
        this.$logger.error("Failed to delete contract info, because: " + error)
        Utils.showFailureSnackbar(this.$t('snackbar:delete-failure', {name: this.$t('common:documentation')}))
      })
      this.$refs.deleteContract.showDialog = false;
      this.$emit('changedItem');
    },

    setRequiredContract(item) {
      this.$refs.contractForm.openNewContractDialog(this.restContracts.options, this.restContracts.search)
      this.$refs.contractForm.parsedType = item.name;
    },

    getRequiredContracts() {
      FacilityTypeService.readRequiredContractTypesForFacilityFromAPI(this.$route.params.id,
          (res) => {
            this.restRequiredContracts = EnumService.translateAndSortEnum(res.data, 'enums:contract-types.');
          }
      )
    },

    getScore() {
      ContractInfoService.readRequiredContractsForFacilityFromAPI(this.$route.params.id,
          (res) => {
            this.score = DataUtils.parseDataToSingleObject(res.data, Score);
            this.$emit('updateContractScore', this.score.percentage);
          }
      )
    },

    openFormDialog() {
      this.$refs.contractForm.openNewContractDialog(this.restContracts.options, this.restContracts.search)
    },

    callUpdateTable() {
      this.$refs.contractTable.callTableUpdate();
    }
  },
  watch: {
    cardView() {
      this.updateContractTable(this.restContracts.options, this.restContracts.search);
    }
  }
}
</script>